import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/button';
import { Link } from 'react-router-dom';

const HeroContainer = styled.section`
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background.gradient};
  overflow: hidden;
  position: relative;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1;
  width: 100%;
  padding: 0 1rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 3rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
  
  .product-name {
    margin-bottom: 0.75rem;
    display: block;
  }
  
  .typed-text {
    color: ${({ theme }) => theme.colors.primary};
    position: relative;
    height: 4.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      height: 3.5rem;
    }
  }
`;

const GradientUnderline = styled(motion.div)`
  height: 4px;
  background: linear-gradient(to right, #9c27b0, #e91e63, #3f51b5);
  width: 200px;
  margin: 0.5rem auto 2rem;
  border-radius: 2px;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin: 0 auto 2.5rem;
  max-width: 700px;
  line-height: 1.6;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.125rem;
    padding: 0 1rem;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    max-width: 300px;
    margin: 1rem auto 0;
  }
  
  button {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
  }
`;



const RotatingText = styled(motion.div)`
  text-align: center;
  width: 100%;
  font-weight: 800;
`;

const HeroSection: React.FC = () => {
  const TEXTS = [
    '40% Faster Tax Preparation',
    'Automate Document Collection',
    'Reduce Filing Errors by 90%',
    'Streamline Client Workflows',
    'Maximize Practice Efficiency'
  ];
  
  const [index, setIndex] = useState(0);
  
  useEffect(() => {
    const intervalId = setInterval(() => {
      setIndex(index => (index + 1) % TEXTS.length);
    }, 3000);
    return () => clearInterval(intervalId);
  }, [TEXTS.length]);
  
  return (
    <HeroContainer>
      <HeroContent>
        <HeroText>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* <span className="product-name">FylePrep:</span> */}
            <span className="typed-text">
              <AnimatePresence mode="wait">
                <RotatingText
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {TEXTS[index]}
                </RotatingText>
              </AnimatePresence>
            </span>
          </HeroTitle>
          
          <GradientUnderline
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            FylePrep helps accounting professionals save time and reduce errors by automating
            document collection, organization, and tax preparation workflows.
          </HeroSubtitle>
          
          <ButtonGroup
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Button size="large" as={Link} to="/waitlist">Get Started</Button>
          </ButtonGroup>
        </HeroText>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;
